// src/components/common/CondoReviewNotification.tsx
// Notification badge + modal for condo review outcomes (approved / declined).
// Mirrors the MatchRequestNotification pattern.

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useWebSocket } from '../../hooks/useWebSocket';

interface CondoReviewEvent {
  condoId: string;
  condoName: string;
  status: 'approved' | 'declined';
  rejectionReason?: string | null;
}

interface CondoNotification {
  id: string; // condoId used as id
  condoName: string;
  status: 'approved' | 'declined';
  rejectionReason?: string | null;
  receivedAt: string;
}

const CondoReviewNotification: React.FC = () => {
  const { user } = useAuth();
  const { onMessage, offMessage, isConnected } = useWebSocket();

  const [notifications, setNotifications] = useState<CondoNotification[]>([]);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!isConnected || !user) return;

    const handleApproved = (data: CondoReviewEvent) => {
      setNotifications((prev) => {
        // Avoid duplicates
        if (prev.some((n) => n.id === data.condoId && n.status === 'approved')) return prev;
        return [
          ...prev,
          {
            id: data.condoId,
            condoName: data.condoName,
            status: 'approved',
            rejectionReason: null,
            receivedAt: new Date().toISOString(),
          },
        ];
      });
    };

    const handleDeclined = (data: CondoReviewEvent) => {
      setNotifications((prev) => {
        if (prev.some((n) => n.id === data.condoId && n.status === 'declined')) return prev;
        return [
          ...prev,
          {
            id: data.condoId,
            condoName: data.condoName,
            status: 'declined',
            rejectionReason: data.rejectionReason ?? null,
            receivedAt: new Date().toISOString(),
          },
        ];
      });
    };

    onMessage('condoReview:approved', handleApproved);
    onMessage('condoReview:declined', handleDeclined);

    return () => {
      offMessage('condoReview:approved');
      offMessage('condoReview:declined');
    };
  }, [isConnected, user, onMessage, offMessage]);

  const handleDismiss = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const unreadCount = notifications.length;

  return (
    <>
      {/* Badge button — only shown when there are notifications */}
      {unreadCount > 0 && (
        <button
          onClick={() => setShowModal(true)}
          className="relative bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors flex items-center space-x-2 shadow-sm"
          aria-label={`${unreadCount} condo review notification${unreadCount !== 1 ? 's' : ''}`}
        >
          <span>🏠 Condo Updates</span>
          <span className="absolute -top-2 -right-2 bg-terracotta text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center border border-coffee">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        </button>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-lg max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-coffee">Condo Review Updates</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-coffee hover:text-terracotta text-2xl transition-colors"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {notifications.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-coffee text-xl mb-2">No new updates</div>
                <div className="text-coffee opacity-80">You're all caught up!</div>
              </div>
            ) : (
              <div className="space-y-4">
                {notifications.map((n) => (
                  <div
                    key={`${n.id}-${n.status}`}
                    className="bg-white border-2 border-coffee rounded-lg p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="text-coffee font-semibold text-lg">{n.condoName}</h3>
                        {n.status === 'approved' ? (
                          <p className="text-green-700 font-medium text-sm mt-1">
                            Your condo has been approved and is now published!
                          </p>
                        ) : (
                          <>
                            <p className="text-red-700 font-medium text-sm mt-1">
                              Your condo submission was returned for revision.
                            </p>
                            {n.rejectionReason && (
                              <p className="text-coffee text-sm mt-2 bg-red-50 border border-red-200 rounded p-2">
                                <span className="font-semibold">Reason: </span>
                                {n.rejectionReason}
                              </p>
                            )}
                          </>
                        )}
                        <p className="text-coffee opacity-50 text-xs mt-2">
                          {new Date(n.receivedAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDismiss(n.id)}
                      className="mt-2 text-sm text-coffee hover:text-terracotta font-medium transition-colors"
                    >
                      Got it
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CondoReviewNotification;
